import type { MetricType } from 'store/settings/types';
import { MetricTypes } from 'store/settings/types';

type AllowedMetricTypes = MetricType;

type AllowedConversions =
  | typeof CONVERSIONS.CAPACITY
  | typeof CONVERSIONS.DENSITY
  | typeof CONVERSIONS.METER
  | typeof CONVERSIONS.MILLIMETER
  | typeof CONVERSIONS.MILLIMETER_TO_FT
  | typeof CONVERSIONS.VOLUME;

/* Returns the current metric for the given conversion type */
export const getMetricUnit = (
  metricType: AllowedMetricTypes,
  conversionType: AllowedConversions,
) => {
  switch (metricType.value) {
    case MetricTypes.IMPERIAL.value:
      return conversionData[conversionType].imperialUnit;
    case MetricTypes.METRIC_SQUARE.value:
      return conversionData[conversionType].metricUnit;
    case MetricTypes.METRIC_ROUND.value:
      return conversionData[conversionType].metricUnit;
    default:
      return conversionData[conversionType].metricUnit;
  }
};

interface MetricInterface {
  value: number | undefined;
  metricType: AllowedMetricTypes;
  conversionType?: AllowedConversions;
  decimals?: number;
}

/* Returns the value on for the given conversion type on the current metric */
export const convertToCurrentMetric = ({
  value,
  metricType,
  conversionType,
  decimals,
}: MetricInterface) => {
  if (typeof value !== 'number') {
    return undefined;
  }

  if (typeof conversionType === 'undefined') {
    return roundToXDecimals(
      value,
      roundToDecimals(value, metricType, conversionType, decimals),
    );
  }

  if (metricType.value === MetricTypes.IMPERIAL.value) {
    return roundToXDecimals(
      value * conversionData[conversionType].factor,
      roundToDecimals(
        value * conversionData[conversionType].factor,
        metricType,
        conversionType,
        decimals,
      ),
    );
  }

  if (
    metricType.value === MetricTypes.METRIC_ROUND.value &&
    (conversionType === CONVERSIONS.MILLIMETER ||
      conversionType === CONVERSIONS.MILLIMETER_TO_FT ||
      conversionType === CONVERSIONS.METER)
  ) {
    return roundToXDecimals(
      value / conversionData[conversionType].roundFactor,
      roundToDecimals(
        value / conversionData[conversionType].roundFactor,
        metricType,
        conversionType,
        decimals,
      ),
    );
  }

  return roundToXDecimals(
    value,
    roundToDecimals(value, metricType, conversionType, decimals),
  );
};

const imperialConversion = (
  value: number | undefined,
  conversionType: string | undefined,
) => {
  switch (conversionType) {
    case CONVERSIONS.CAPACITY:
    case CONVERSIONS.DENSITY:
      if (typeof value === 'number' && value < 1) {
        return 1;
      }
      return 0;
    case CONVERSIONS.MILLIMETER:
    case CONVERSIONS.MILLIMETERNOROUNDCONV:
      if (typeof value !== 'number') {
        return 3;
      }

      if (value < 0.01) {
        return 4;
      } else if (value >= 0.01 && value < 1) {
        return 3;
      } else if (value >= 1 && value < 50) {
        return 2;
      } else {
        return 1;
      }

    case CONVERSIONS.MILLIMETER_TO_FT:
    case CONVERSIONS.METER:
    case CONVERSIONS.VOLUME:
      if (typeof value !== 'number') {
        return 1;
      }

      if (value < 10) {
        return 2;
      } else {
        return 1;
      }

    default:
      return 3;
  }
};

const metricConversion = (
  value: number | undefined,
  conversionType: string | undefined,
) => {
  switch (conversionType) {
    case CONVERSIONS.CAPACITY:
    case CONVERSIONS.METER:
    case CONVERSIONS.VOLUME:
      if (typeof value !== 'number') {
        return 0;
      }
      if (value < 1) {
        return 1;
      } else {
        return 0;
      }
    case CONVERSIONS.MILLIMETER:
    case CONVERSIONS.MILLIMETERNOROUNDCONV:
      if (typeof value !== 'number') {
        return 0;
      }
      if (value < 0.01) {
        return 3;
      } else if (value >= 0.01 && value < 1) {
        return 2;
      } else if (value >= 1 && value < 50) {
        return 1;
      } else {
        return 0;
      }
    case CONVERSIONS.MILLIMETER_TO_FT:
    case CONVERSIONS.DENSITY:
      if (typeof value !== 'number') {
        return 1;
      }
      if (value < 10) {
        return 2;
      } else {
        return 1;
      }
    default:
      return 0;
  }
};

export const roundToDecimals = (
  value: number | undefined,
  metricType: AllowedMetricTypes,
  conversionType: string | undefined,
  decimals: number | undefined,
): number => {
  if (!(typeof decimals !== 'number' || decimals < 0)) {
    return decimals;
  }
  if (metricType.value === MetricTypes.IMPERIAL.value) {
    return imperialConversion(value, conversionType);
  }
  return metricConversion(value, conversionType);
};

/* Returns the converted value from the current to the default metric for the given conversion type*/
export const convertToDefaultMetric = ({
  value,
  metricType,
  conversionType,
}: MetricInterface) => {
  if (!(typeof value === 'number' && typeof conversionType !== 'undefined')) {
    return value;
  }
  if (metricType.value === MetricTypes.IMPERIAL.value) {
    return value / conversionData[conversionType].factor;
  }
  if (metricType.value === MetricTypes.METRIC_ROUND.value) {
    return value * conversionData[conversionType].roundFactor;
  }

  return value;
};

/* Function to round value to X decimals */
export const roundToXDecimals = (
  value: number | string,
  decimals: number,
): number => {
  if (typeof value === 'number') {
    return parseFloat(value.toFixed(decimals));
  }

  const floatValue = parseFloat(value);
  return parseFloat(floatValue.toFixed(decimals));
};

export const CONVERSIONS = {
  DENSITY: 'DENSITY',
  CAPACITY: 'CAPACITY',
  MILLIMETER: 'MILLIMETER',
  MILLIMETERNOROUNDCONV: 'MILLIMETERNOROUNDCONV',
  MILLIMETER_TO_FT: 'MILLIMETER_TO_FT',
  METER: 'METER',
  METERNOROUNDCONV: 'METERNOROUNDCONV',
  AREA: 'AREA',
  VOLUME: 'VOLUME',
  WEIGHT: 'WEIGHT',
  POWER: 'POWER',
};

export const conversionData = {
  [CONVERSIONS.DENSITY]: {
    metricUnit: 't/m3',
    imperialUnit: 'lbs/ft3',
    factor: 62.427962,
    roundFactor: 1,
  },
  [CONVERSIONS.CAPACITY]: {
    metricUnit: 'mtph',
    imperialUnit: 'stph',
    factor: 1.102311,
    roundFactor: 1,
  },
  [CONVERSIONS.MILLIMETER]: {
    metricUnit: 'mm',
    imperialUnit: 'in',
    factor: 0.03937,
    roundFactor: 0.8,
  },
  [CONVERSIONS.MILLIMETERNOROUNDCONV]: {
    metricUnit: 'mm',
    imperialUnit: 'in',
    factor: 0.03937,
    roundFactor: 1,
  },
  [CONVERSIONS.MILLIMETER_TO_FT]: {
    metricUnit: 'mm',
    imperialUnit: 'ft',
    factor: 0.003281,
    roundFactor: 1,
  },
  [CONVERSIONS.METER]: {
    metricUnit: 'm',
    imperialUnit: 'ft',
    factor: 3.281,
    roundFactor: 0.8,
  },
  [CONVERSIONS.METERNOROUNDCONV]: {
    metricUnit: 'm',
    imperialUnit: 'ft',
    factor: 3.281,
    roundFactor: 1,
  },
  [CONVERSIONS.AREA]: {
    metricUnit: 'm2',
    imperialUnit: 'ft2',
    factor: 10.7639,
    roundFactor: 1,
  },
  [CONVERSIONS.VOLUME]: {
    metricUnit: 'm3',
    imperialUnit: 'ft3',
    factor: 35.3146667,
    roundFactor: 1,
  },
  [CONVERSIONS.WEIGHT]: {
    metricUnit: 'kg',
    imperialUnit: 'lb',
    factor: 2.2,
    roundFactor: 1,
  },
  [CONVERSIONS.POWER]: {
    metricUnit: 'kW',
    imperialUnit: 'hp',
    factor: 1.34102, // kW to horsepower (metric)
    roundFactor: 1,
  },
};

/**
 * Converts metric units in any text string based on the current metric type.
 * This is a general-purpose utility that can be used for warnings, labels, descriptions, etc.
 *
 * Handles patterns like:
 * - "< 4 mm" → "< 0.16 in" (Imperial)
 * - "117 mtph" → "129 stph" (Imperial)
 * - "2.5 t/m3" → "156 lbs/ft3" (Imperial)
 * - Values with METRIC_ROUND apply round holes conversion (÷ 0.8)
 *
 * @param text - Any text string that may contain metric values
 * @param metricType - The current metric type (METRIC_SQUARE, METRIC_ROUND, or IMPERIAL)
 * @returns The text with units converted based on the metric type
 */
export const convertUnitsInText = (
  text: string,
  metricType: MetricType,
): string => {
  // If metric type is METRIC_SQUARE, return text as-is (already in metric)
  if (metricType.value === MetricTypes.METRIC_SQUARE.value) {
    return text;
  }

  let convertedText = text;

  // Define conversion patterns: [regex, conversionType, metricUnit, includeSpace]
  const conversionPatterns: Array<{
    regex: RegExp;
    conversionType: string;
    metricUnit: string;
    includeSpace: boolean;
  }> = [
    // Millimeters (with or without space before unit)
    {
      regex: /(\d+(?:\.\d+)?)\s*(mm)(?=\s|$|[,.])/gi,
      conversionType: CONVERSIONS.MILLIMETER,
      metricUnit: 'mm',
      includeSpace: false,
    },
    // Meters (with or without space before unit)
    {
      regex: /(\d+(?:\.\d+)?)\s*(m)(?=\s|$|[,.]|[^a-z])/gi,
      conversionType: CONVERSIONS.METER,
      metricUnit: 'm',
      includeSpace: true,
    },
    // Capacity: mtph → stph
    {
      regex: /(\d+(?:\.\d+)?)\s*(mtph)/gi,
      conversionType: CONVERSIONS.CAPACITY,
      metricUnit: 'mtph',
      includeSpace: true,
    },
    // Density: t/m3 or t/m³ → lbs/ft3 or lbs/ft³
    {
      regex: /(\d+(?:\.\d+)?)\s*(t\/m[3³])/gi,
      conversionType: CONVERSIONS.DENSITY,
      metricUnit: 't/m3',
      includeSpace: true,
    },
  ];

  // Apply each conversion pattern
  conversionPatterns.forEach(
    ({ regex, conversionType, metricUnit: _metricUnit, includeSpace }) => {
      convertedText = convertedText.replace(regex, (match, valueStr, _unit) => {
        const value = parseFloat(valueStr);
        const converted = convertToCurrentMetric({
          value,
          metricType,
          conversionType,
          decimals: 2,
        });

        if (typeof converted === 'number') {
          const newUnit = getMetricUnit(metricType, conversionType);
          const space = includeSpace ? ' ' : '';
          return `${converted}${space}${newUnit}`;
        }

        return match; // Return original if conversion failed
      });
    },
  );

  return convertedText;
};
